import useDebounce from "@/hooks/useDebounce";
import { fetchFeaturedHotel, findHotelByName } from "@/services/hotel.service";
import { CircularProgress, FormControl } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import { ChangeEvent, FC, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./styles.css";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, X } from "lucide-react";

interface IProps {
  values?: string[];
  onChange: (value: string[]) => void;
}

const SelectHotels: FC<IProps> = ({ onChange, values }) => {
  const [open, setOpen] = useState(false);
  const [textSearch, setTextSearch] = useState<string>("");
  const query = useDebounce(textSearch, 300);

  const { data: dataSelect, isLoading: isLoadingDataSelected } = useQuery({
    queryKey: ["fetchFeaturedHotel", query],
    queryFn: () => fetchFeaturedHotel(values || []),
    enabled: values && values.length > 0,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["findHotelByName", query],
    queryFn: () => findHotelByName(query),
  });

  const listSelect = uniqBy(
    [...((values?.length === 0 ? [] : dataSelect) || []), ...(data || [])],
    "hotelId",
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setTextSearch(event.target.value);
  };

  const handleSelectHotel = (value: string) => {
    const listSelected = values?.includes(value)
      ? values.filter((item) => item !== value)
      : [...(values || []), value];

    onChange(listSelected);
  };

  const renderValue = () => {
    const labels = listSelect
      ?.filter((item) => values?.some((id) => id === `${item.hotelId}`))
      .map((item) => ({ hotelName: item.hotelName, hotelId: item.hotelId }));

    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 py-1">
        {(isLoading || isLoadingDataSelected) && (
          <div className="flex items-center justify-center py-2">
            <CircularProgress size="12px" color="inherit" />
          </div>
        )}
        {labels?.length ? (
          labels.map((item, index) => (
            <div
              key={index}
              className="flex max-w-full items-center gap-1 rounded-[4px] bg-[#E0E7FF] px-2 py-1"
            >
              <p className="line-clamp-1 text-wrap text-[10px] font-medium leading-4 text-[#322F80]">
                {item.hotelName}
              </p>

              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSelectHotel(`${item.hotelId}`);
                }}
              >
                <X className="size-3 text-[#322F80]" />
              </div>
            </div>
          ))
        ) : (
          <span className="text-sm text-text">Select Hotels</span>
        )}
      </div>
    );
  };

  return (
    <FormControl className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex cursor-pointer items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-4 py-3">
            <div>
              {values?.length ? (
                renderValue()
              ) : (
                <span className="text-sm text-text">Select Hotels</span>
              )}
            </div>
            <KeyboardArrowDownIcon
              className=""
              sx={{
                color: "#94A3B8",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-full"
          style={{
            width: "var(--radix-popover-trigger-width)",
          }}
        >
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Search hotels..."
              value={textSearch}
              onChange={handleSearch}
              className={cn(
                "placeholder:text-[#94A3B8], h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal",
              )}
            />
            <div className="flex max-h-[270px] flex-col  overflow-y-auto">
              {isLoading && (
                <div className="flex items-center justify-center py-2">
                  <CircularProgress size="22px" color="inherit" />
                </div>
              )}

              {listSelect?.map((hotel, index) => (
                <div
                  key={index}
                  className="flex cursor-pointer items-center rounded-md p-2 hover:bg-zinc-100"
                  onClick={() => {
                    handleSelectHotel(`${hotel.hotelId}`);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      values?.includes(`${hotel.hotelId}`)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <span className="text-sm text-[#475569]">
                    {hotel.hotelName}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};

export default SelectHotels;
